/**
 *
 *  Trust & Safety Page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 18/07/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Title, Meta } from "react-meta-elements";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";

const TabButton = ({ className, active, label, onClick }) => {
  const handleClick = useCallback(() => {
    onClick(label);
  }, [label, onClick]);

  return (
    <button
      className={`${
        className ? className + " " : ""
      }tbk-rounded-t-2xl tbk-text-small-bold md:tbk-text-h3-subtitle tbk-uppercase_ hover:tbk-underline_ tbk-p-2 hover:tbk-bg-coral hover:tbk-text-basic-white ${
        active
          ? "tbk-underline_ tbk-bg-coral tbk-text-basic-white"
          : "tbk-bg-blue-grey-light tbk-text-primary"
      }`}
      onClick={handleClick}
    >
      {label}
    </button>
  );
};

export default function TrustSafetyPage() {
  const [activeTab, setActiveTab] = useState("Overview");

  const handleTabClick = useCallback(
    (tabLabel) => {
      if (tabLabel !== activeTab) {
        setActiveTab(tabLabel);
      }
    },
    [activeTab]
  );

  return (
    <>
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content={`On-demand, in person, personal training in London.`}
      />
      <Header showLinks={true} />

      <div className="tbk-bg-grey-light tbk-pb-5">
        <div className="tbk-mb-5 tbk-flex tbk-h-32 tbk-items-center tbk-justify-center tbk-bg-trust-safety-page-head-mobile tbk-bg-cover tbk-bg-no-repeat tbk-text-center md:tbk-h-44 md:tbk-bg-trust-safety-page-head">
          <h1 className="tbk-text-h2-black md:tbk-text-epic-bold tbk-text-basic-white">
            Trust & Safety
          </h1>
        </div>

        <div className="tbk-container tbk-mx-auto tbk-px-0 lg:tbk-px-2">
          <div className="tbk-flex tbk-flex-col tbk-items-center">
            <div className="tbk-inline-flex tbk-w-full tbk-border-b-4 tbk-border-solid tbk-border-coral lg:tbk-w-210">
              <TabButton
                className="tbk-mr-0.5 tbk-grow tbk-uppercase md:tbk-mr-1 lg:tbk-normal-case"
                label="Overview"
                active={activeTab === "Overview"}
                onClick={handleTabClick}
              />
              <TabButton
                className="tbk-mr-0.5 tbk-grow tbk-uppercase md:tbk-mr-1 lg:tbk-normal-case"
                label="Member"
                active={activeTab === "Member"}
                onClick={handleTabClick}
              />
              <TabButton
                className="tbk-mr-0.5 tbk-grow tbk-uppercase md:tbk-mr-1 lg:tbk-normal-case"
                label="Coach"
                active={activeTab === "Coach"}
                onClick={handleTabClick}
              />
              <TabButton
                className="tbk-grow tbk-uppercase lg:tbk-normal-case"
                label="Payments"
                active={activeTab === "Payments"}
                onClick={handleTabClick}
              />
            </div>

            {activeTab === "Overview" ? (
              <div className="tbk-rounded-b-2xl tbk-bg-basic-white tbk-py-5 tbk-px-2 tbk-shadow-trube md:tbk-px-7 lg:tbk-w-210">
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Trust & Safety Overview
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Each month,{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  on the TruBe network carry out services across a variety of
                  fitness and wellness disciplines. We take great pride in being
                  the foremost platform for on-demand, mobile fitness and
                  wellness. As your well-being is our utmost concern, we go the
                  extra mile to guarantee that every TruBe session is both
                  secure and rejuvenating.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Vetted professionals
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  All{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  are fully licensed and certified, including fitness & wellness{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  who have completed rigorous training and course work.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Always protected
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  We verify that all{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  are fully insured. This guarantees that both the client and{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" /> are
                  cared for in the event of unexpected injury or property
                  damage.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Background checked
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  The safety of our members and{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  on the network is of utmost importance to us. To ensure this,
                  we conduct thorough background checks and continually monitor
                  criminal records of all{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />.
                  Additionally, we verify the information of our clients before
                  every service to maintain a secure environment.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  365 day support
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Whether you have questions about the app, booking your
                  appointment, or any of the services in general, our team is
                  available via email, and online chat, every day of the year
                  (including holidays).
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Secure payment
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Our secure platform ensures your money gets to your
                  <FormattedMessage id="trainer" defaultMessage="trainer" />
                  —that’s why we always ask you to pay through TruBe to
                  guarantee you get a receipt and a protected transfer of funds.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Review system
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  After every appointment, members and{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  are asked to rate their experience anonymously. Our Customer
                  Service team reviews ratings below 4 stars to maintain the
                  safety and quality of the TruBe platform.
                </p>
              </div>
            ) : activeTab === "Member" ? (
              <div className="tbk-rounded-b-2xl tbk-bg-basic-white tbk-py-5 tbk-px-2 tbk-shadow-trube md:tbk-px-7 lg:tbk-w-210">
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Member Safety
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  We take extra steps to provide a safe and stress-free
                  experience every time you book. That’s why we only refer you
                  to{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  who are experts in their craft. Here’s what you can do to
                  ensure your safety and health:
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Communicate
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Let your{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />{" "}
                  know in advance and during the service which areas to focus on
                  to better tailor the experience to your needs. In the case of
                  fitness, be sure to let your{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />{" "}
                  know if you have any pre existing injuries or health
                  conditions
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Stop if necessary
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  If you feel uncomfortable during your service and wish to
                  stop, simply communicate this to your{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" /> by
                  saying, “I’d like to discontinue the service at this time.”
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Always pay and communicate within the app
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Submitting payment and communicating with your{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />{" "}
                  through the TruBe app protects your information and theirs.
                  You should never wire money, provide credit card information,
                  or otherwise pay a{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />{" "}
                  directly. If you are asked to do so, please report it to us
                  immediately.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Reach out to our team
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  We’re happy to chat with you before or after your appointment
                  to answer your questions or concerns. Simply contact our
                  Customer Service Team once you have booked and request a pre-
                  or post-appointment check-in.
                </p>
              </div>
            ) : activeTab === "Coach" ? (
              <div className="tbk-rounded-b-2xl tbk-bg-basic-white tbk-py-5 tbk-px-2 tbk-shadow-trube md:tbk-px-7 lg:tbk-w-210">
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  <FormattedMessage id="Trainer" defaultMessage="Trainer" />{" "}
                  Safety
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  At the heart of the TruBe experience is a network of highly
                  skilled and dedicated{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />.
                  We appreciate you sharing your passion for fitness & wellness,
                  and we’ve worked hard to build a platform that makes your
                  safety our top priority. Follow the below guidelines to ensure
                  your safety with TruBe
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Perform a thorough intake
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Review the expectations for the session beforehand by
                  confirming the details of the session. For fitness
                  professionals, this includes reviewing conditions, confirming
                  the discipline, and areas of focus.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Stop when necessary
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  If you feel uncomfortable you can always refuse to enter the
                  client’s home or discontinue the service midway through. You
                  will never be penalized for leaving a TruBe session if you
                  feel unsafe. In an escalating situation, prioritize your
                  personal safety, get out, leave your equipment, and contact
                  TruBe as soon as you are safe. We will replace or reimburse
                  you for abandoned equipment if we are not able to recover for
                  you.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Use our secure client communication
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Do not give out your contact information, or communicate with
                  clients outside the TruBe app. This is considered breach of
                  contract and could result in removal from the network. When
                  you contact your client through the app, TruBe protects your
                  contact information by acting as a bridge between you and the
                  client.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Accept secure payments through TruBe
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Payments usually reach your account within 24-72 hours after
                  completed sessions. Transactions are secured from start to
                  finish using 256-bit encryption and best-in-class security
                  tools.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Contact us, anytime
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  TruBe provides support for{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  365 days a year via in-app communication, chat and email.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Report unsatisfactory experiences
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  If a TruBe appointment makes you feel uncomfortable in any
                  way, contact{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />{" "}
                  support. We continuously review reports and suspend suspicious
                  clients in an effort to protect the TruBe
                  <FormattedMessage
                    id="trainer"
                    defaultMessage="trainer"
                  />{" "}
                  experience and the network as a whole.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Be the best kind of TruBe{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  The TruBe community relies on both{" "}
                  <FormattedMessage id="trainers" defaultMessage="trainers" />{" "}
                  and clients to ensure health, safety, and well-being for
                  everyone.
                </p>
              </div>
            ) : (
              <div className="tbk-rounded-b-2xl tbk-bg-basic-white tbk-py-5 tbk-px-2 tbk-shadow-trube md:tbk-px-7 lg:tbk-w-210">
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Payment Safety
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  With TruBe, you’re connected to a behind-the-scenes team that
                  is dedicated to providing you with a safe and relaxing
                  experience from beginning to end. That’s why all transactions,
                  including booking and payment must be completed through the
                  TruBe App. This secure platform helps our team ensure that
                  your safety and information are always protected.
                  <br />
                  <br />
                  <i>
                    Follow the below tips to avoid fraudulent practices and keep
                    your information secure:
                  </i>
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  What should I do if someone asks me to pay outside the TruBe
                  App?
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  You should never be asked to wire money, provide credit card
                  information, or otherwise pay a{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />{" "}
                  directly. If you are, there are easy ways to report it to us
                  to ensure your information, finances, and identity are
                  protected.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Reporting requests for payment outside of TruBe
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  If a TruBe member or{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />{" "}
                  asks you to pay off-site or through another company, report it
                  to us in the following way:
                  <br />
                  <br />
                  *Email us at support@trube.app
                  <br />
                  <br />
                  Unless you are working with TruBe at Work Events, you should
                  never receive invoices from TruBe, in the form of paper, PDF
                  or otherwise. If someone attempts to complete a transaction
                  through any of these methods, please report it immediately:
                  Western Union, MoneyGram, Liberty Reserve, cashier’s check,
                  money order, cash transaction.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Underpayment scam
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  If someone offers to pay or receive less than the given price
                  for a TruBe service, it is no longer possible for TruBe to
                  guarantee secure payment as it is no longer considered a TruBe
                  session.
                </p>
                <h2 className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  Advancement scam
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  If someone offers to pay or receive payment for future
                  services outside of the TruBe App, TruBe can no longer
                  guarantee fulfillment of those appointments. You can buy TruBe
                  packages directly through the app and easily rebook with the
                  same{" "}
                  <FormattedMessage id="trainer" defaultMessage="trainer" />.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
